/**
 * Shared HTML template generator for custom reports
 * Used for both PDF export and email reports
 *
 * NOTE: A copy of this template exists in supabase/functions/process-scheduled-reports/index.ts
 * If you modify this template, update the edge function template as well.
 */

export interface ReportField {
  field: string;
  label: string;
  format?: 'text' | 'number' | 'currency' | 'date' | 'boolean';
}

export interface ReportConfig {
  fields: ReportField[];
  groupBy?: Array<{ field: string }>;
  aggregations?: Array<{ type: string; field: string; alias: string }>;
}

export interface ReportData {
  name: string;
  config: ReportConfig;
  data: any[];
}

export interface ReportHtmlOptions {
  title?: string;
  subtitle?: string;
  includeSummary?: boolean;
  maxPreviewRows?: number; // If set, only show this many rows with "...and X more" message
  forPrint?: boolean; // Add print-specific styles
}

function formatValue(value: any, format?: string): string {
  if (value === null || value === undefined) return '-';

  switch (format) {
    case 'currency':
      return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'number':
      return Number(value).toLocaleString('en-US');
    case 'date':
      if (!value) return '-';
      return new Date(value).toLocaleDateString('en-US');
    case 'boolean':
      return value ? 'Yes' : 'No';
    default:
      return String(value);
  }
}

function formatColumnName(col: string): string {
  return col
    .replace(/_/g, ' ')
    .replace(/\bid\b/gi, 'ID')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function generateSummary(data: any[], fields: ReportField[]): { totalRecords: number; availableCount?: number; totalValue?: number } {
  const summary: { totalRecords: number; availableCount?: number; totalValue?: number } = {
    totalRecords: data.length
  };

  if (data.length === 0) return summary;

  // Count by status if status field exists
  const hasStatus = fields.some(f => f.field === 'status');
  if (hasStatus) {
    summary.availableCount = data.filter(r => r.status === 'Available').length;
  }

  // Sum cost if cost field exists
  const costField = fields.find(f => f.field === 'cost' || f.format === 'currency');
  if (costField) {
    summary.totalValue = data.reduce((sum, r) => sum + (parseFloat(r[costField.field]) || 0), 0);
  }

  return summary;
}

export function generateReportHtml(report: ReportData, options: ReportHtmlOptions = {}): string {
  const {
    title = report.name,
    subtitle,
    includeSummary = true,
    maxPreviewRows,
    forPrint = false,
  } = options;

  const { data, config } = report;
  const fields = config.fields || [];
  const columns: ReportField[] = fields.length > 0 ? fields : (data.length > 0 ? Object.keys(data[0]).map(k => ({ field: k, label: formatColumnName(k) })) : []);
  const summary = generateSummary(data, columns as ReportField[]);
  const displayRows = maxPreviewRows ? data.slice(0, maxPreviewRows) : data;
  const remainingRows = maxPreviewRows ? data.length - maxPreviewRows : 0;

  const printStyles = forPrint ? `
    @page { margin: 0.5in; }
    @media print {
      body { margin: 0; }
      .no-print { display: none; }
    }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
  ` : '';

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${title}</title>
<style>
${printStyles}
body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 900px; margin: 0 auto; padding: 20px; }
.header { background: linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%); color: white; padding: 20px; border-radius: 8px 8px 0 0; }
.header h1 { margin: 0; font-size: 24px; }
.header .subtitle { margin: 8px 0 0; opacity: 0.9; font-size: 14px; }
.content { background: #f9fafb; padding: 20px; border: 1px solid #e5e7eb; }
.summary-grid { display: flex; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
.summary-card { background: white; padding: 16px; border-radius: 8px; border: 1px solid #e5e7eb; flex: 1; min-width: 120px; text-align: center; }
.summary-title { font-size: 12px; color: #6b7280; margin-bottom: 4px; text-transform: uppercase; }
.summary-value { font-size: 24px; font-weight: bold; color: #1f2937; }
.summary-value.green { color: #10b981; }
.data-table { width: 100%; border-collapse: collapse; margin-top: 16px; background: white; font-size: 13px; }
.data-table th { background: #f3f4f6; padding: 10px 8px; text-align: left; font-size: 11px; font-weight: 600; border-bottom: 2px solid #e5e7eb; text-transform: uppercase; }
.data-table td { padding: 8px; border-bottom: 1px solid #e5e7eb; }
.data-table tr:nth-child(even) { background: #f9fafb; }
.footer { padding: 20px; text-align: center; color: #6b7280; font-size: 12px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px; background: white; }
.more-rows { color: #6b7280; font-size: 13px; padding: 12px; text-align: center; }
</style>
</head>
<body>
<div class="header">
<h1>${title}</h1>
<p class="subtitle">${subtitle || `Generated on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}</p>
</div>
<div class="content">
${includeSummary ? `
<div class="summary-grid">
<div class="summary-card">
<div class="summary-title">Total Records</div>
<div class="summary-value">${summary.totalRecords}</div>
</div>
${summary.availableCount !== undefined ? `
<div class="summary-card">
<div class="summary-title">Available</div>
<div class="summary-value green">${summary.availableCount}</div>
</div>
` : ''}
${summary.totalValue !== undefined ? `
<div class="summary-card">
<div class="summary-title">Total Value</div>
<div class="summary-value">$${summary.totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
</div>
` : ''}
</div>
` : ''}
${data.length > 0 ? `
<table class="data-table">
<thead>
<tr>
${columns.map(col => `<th>${col.label || formatColumnName(col.field)}</th>`).join('')}
</tr>
</thead>
<tbody>
${displayRows.map(row => `
<tr>
${columns.map(col => `<td>${formatValue(row[col.field], col.format)}</td>`).join('')}
</tr>
`).join('')}
</tbody>
</table>
${remainingRows > 0 ? `<p class="more-rows">...and ${remainingRows} more records</p>` : ''}
` : '<p>No records found matching your report criteria.</p>'}
</div>
<div class="footer">
<p>This is an automated report from TrustPuma.</p>
</div>
${forPrint ? `
<script>
window.onload = function() { window.print(); };
</script>
` : ''}
</body>
</html>`;

  return html;
}

/**
 * Export report to PDF using browser print functionality
 */
export function exportReportToPdf(report: ReportData, options: ReportHtmlOptions = {}): void {
  const htmlContent = generateReportHtml(report, { ...options, forPrint: true });

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.error('Failed to open print window. Please allow popups for this site.');
    return;
  }

  printWindow.document.write(htmlContent);
  printWindow.document.close();
}
